import { createContext, useContext, useEffect, useRef, useState, type CSSProperties, type PointerEvent } from 'react';

export type MenuStage = 'hidden' | 'rail' | 'full';
type DeviceClass = 'phone' | 'tablet' | 'desktop';
const stages: MenuStage[] = ['hidden', 'rail', 'full'];
const storageKey = (device: DeviceClass) => `amoye.standard-menu.${device}`;
function deviceClass(): DeviceClass {
  if (window.matchMedia?.('(max-width: 42rem)').matches) return 'phone';
  return window.matchMedia?.('(max-width: 68rem)').matches
    || window.matchMedia?.('(pointer: coarse) and (max-width: 1399px)').matches ? 'tablet' : 'desktop';
}
function savedStage(device: DeviceClass): MenuStage {
  try {
    const saved = localStorage.getItem(storageKey(device));
    if (stages.includes(saved as MenuStage)) return saved as MenuStage;
  } catch { /* Local presentation preferences are optional. */ }
  return device === 'desktop' ? 'full' : 'rail';
}
export function snapMenuStage(offset: number, rail: number, full: number): MenuStage {
  return offset < rail / 2 ? 'hidden' : offset < (rail + full) / 2 ? 'rail' : 'full';
}

/** Only presentation state. Never contains account, taxpayer or authority identifiers. */
export function useStandardMenu(enabled: boolean) {
  const [device, setDevice] = useState(deviceClass);
  const [stage, setStage] = useState<MenuStage>(() => savedStage(deviceClass()));
  const [host, setHost] = useState<HTMLDivElement | null>(null);
  const [dragOffset, setDragOffset] = useState<number>();
  const direction = useRef<1 | -1>(stage === 'full' ? -1 : 1);
  const drag = useRef<{ startX: number; startOffset: number; moved: boolean } | undefined>(undefined);
  const suppressClick = useRef(false);
  const control = useRef<HTMLButtonElement>(null);
  const active = enabled && device !== 'phone';
  const rail = device === 'tablet' ? 84 : 72;
  const full = device === 'tablet' ? 268 : 236;
  const offset = active ? dragOffset ?? (stage === 'full' ? full : stage === 'rail' ? rail : 0) : 0;
  useEffect(() => {
    const queries = ['(max-width: 42rem)', '(max-width: 68rem)', '(pointer: coarse) and (max-width: 1399px)']
      .map((query) => window.matchMedia?.(query));
    const update = () => {
      const next = deviceClass();
      const restored = savedStage(next);
      direction.current = restored === 'full' ? -1 : 1;
      setDevice(next); setStage(restored); setDragOffset(undefined); drag.current = undefined;
    };
    queries.forEach((query) => query?.addEventListener('change', update));
    return () => queries.forEach((query) => query?.removeEventListener('change', update));
  }, []);
  const select = (next: MenuStage) => {
    if (next === 'hidden') direction.current = 1;
    if (next === 'full') direction.current = -1;
    setStage(next);
    try { localStorage.setItem(storageKey(device), next); } catch { /* Optional preference. */ }
  };
  const nextStage = stage === 'hidden' ? 'rail' : stage === 'full' ? 'rail' : direction.current === 1 ? 'full' : 'hidden';
  const pointerDown = (event: PointerEvent<HTMLElement>) => {
    if (!active || event.button !== 0) return;
    suppressClick.current = false;
    drag.current = { startX: event.clientX, startOffset: offset, moved: false };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const pointerMove = (event: PointerEvent<HTMLElement>) => {
    if (!drag.current) return;
    const delta = event.clientX - drag.current.startX;
    if (Math.abs(delta) < 6 && !drag.current.moved) return;
    drag.current.moved = true;
    setDragOffset(Math.max(0, Math.min(full, drag.current.startOffset + delta)));
  };
  const pointerUp = (event: PointerEvent<HTMLElement>) => {
    if (!drag.current) return;
    if (drag.current.moved) {
      suppressClick.current = true;
      select(snapMenuStage(Math.max(0, Math.min(full, drag.current.startOffset + event.clientX - drag.current.startX)), rail, full));
    }
    drag.current = undefined; setDragOffset(undefined);
  };
  const pointerCancel = () => { drag.current = undefined; setDragOffset(undefined); suppressClick.current = true; };
  const style = {
    '--am-menu-offset': `${offset}px`, '--am-menu-rail': `${rail}px`, '--am-menu-full': `${full}px`,
    '--am-menu-reveal': Math.min(1, offset / rail),
    '--am-menu-label-reveal': Math.max(0, Math.min(1, (offset - rail) / (full - rail))),
  } as CSSProperties;
  return { active, device, stage, host, setHost, control, style, offset, dragging: dragOffset !== undefined,
    collapsed: stage !== 'full', hidden: offset === 0,
    triggerProps: {
      'aria-label': `Navigation: ${stage}. Show ${nextStage === 'hidden' ? 'no menu' : nextStage === 'rail' ? 'icons' : 'icons and names'}`,
      'aria-expanded': stage !== 'hidden', 'aria-controls': 'am-standard-menu',
      onClick: () => { if (suppressClick.current) { suppressClick.current = false; return; } select(nextStage); },
      onKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          select(stages[Math.max(0, Math.min(2, stages.indexOf(stage) + (event.key === 'ArrowRight' ? 1 : -1)))]!);
        }
      },
    },
    dragProps: { onPointerDown: pointerDown, onPointerMove: pointerMove, onPointerUp: pointerUp, onPointerCancel: pointerCancel },
  };
}

const StandardMenuContext = createContext<ReturnType<typeof useStandardMenu> | undefined>(undefined);
export const StandardMenuProvider = StandardMenuContext.Provider;
export const useStandardMenuContext = () => useContext(StandardMenuContext);
